/**
 * SSO Link - 기존 계정 연동 화면
 * 
 * 정보 제공 동의 후 기존 메디크루 계정과 SSO를 연동하는 화면
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, AlertDialog } from '@/components/shared/ui';
import { getSsoInfo, connectSso, ssoLogin } from '@/services/ssoService';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/data/queries/useUser';
import { convertRoleNumberToString, resolveLicenseFromProfile } from '@/utils/roleUtils';
import { ROUTES } from '@/lib/constants/routes';
import type { SsoUserDto, UserSsoAccessDto } from '@/types/api/sso';

const medicrewLogo = '/assets/images/brand/medicrew_blue_logo.png';

export default function SsoLinkConnect(): React.ReactElement {
  const { origin, userSsoAccessId } = useParams<{ origin: string; userSsoAccessId: string }>();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { getProfile } = useUser();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState<SsoUserDto | null>(null);
  const [userSsoAccess, setUserSsoAccess] = useState<UserSsoAccessDto | null>(null);
  const [alertState, setAlertState] = useState<{ isOpen: boolean; message: string }>({
    isOpen: false,
    message: '',
  });

  const showAlert = (message: string) => {
    setAlertState({ isOpen: true, message });
  };

  const closeAlert = () => {
    setAlertState({ isOpen: false, message: '' });
  };

  useEffect(() => {
    const loadSsoInfo = async () => {
      if (!origin || origin !== 'kha') {
        showAlert('지원하지 않는 SSO 제공자입니다.');
        setIsLoading(false);
        return;
      }

      if (!userSsoAccessId || isNaN(Number(userSsoAccessId))) {
        showAlert('잘못된 SSO 접근 정보입니다.');
        setIsLoading(false);
        return;
      }

      const userSsoAccessIdNum = Number(userSsoAccessId);

      try {
        const ssoInfo = await getSsoInfo(userSsoAccessIdNum);

        if (!ssoInfo.user) {
          showAlert('연동할 메디크루 계정을 찾을 수 없습니다.');
          setIsLoading(false);
          return;
        }

        setUser(ssoInfo.user);
        setUserSsoAccess(ssoInfo.userSsoAccess);
        setIsLoading(false);
      } catch (error: any) {
        showAlert(error.message || 'SSO 정보를 불러오는데 실패했습니다.');
        setIsLoading(false);
      }
    };

    loadSsoInfo();
  }, [origin, userSsoAccessId]);

  const handleConnect = async () => {
    if (!user || !userSsoAccess) return;

    setIsSubmitting(true);

    try {
      await connectSso({
        userSsoAccessId: userSsoAccess.id,
        userId: user.id,
      });

      const loginResponse = await ssoLogin({
        userSsoAccessId: userSsoAccess.id,
      });

      localStorage.setItem('medicrew_access_token', loginResponse.accessToken);
      if (loginResponse.refreshToken?.trim()) {
        localStorage.setItem('medicrew_refresh_token', loginResponse.refreshToken.trim());
      } else {
        localStorage.removeItem('medicrew_refresh_token');
      }

      const userProfile = await getProfile();

      const userRole = convertRoleNumberToString(userProfile.role);
      const userLicense = resolveLicenseFromProfile(userProfile);

      const organizationId = userProfile.organizationId != null ? String(userProfile.organizationId) : null;
      const organizationName = userProfile.organizationName?.trim() ?? '';

      const displayName =
        userProfile.realName && userProfile.realName.trim() ? userProfile.realName.trim() : user.loginId;

      const currentAccountEntry = {
        accountId: `acc_${userProfile.userId}`,
        loginId: user.loginId,
        displayName,
        email: userProfile.email || '',
        profileImageUrl: userProfile.profileImageUrl,
        phoneNumber: userProfile.phoneNumber?.trim() ?? '',
        affiliation: userProfile.affiliation?.trim() ?? '',
        position: userProfile.position?.trim() ?? '',
        department: userProfile.department?.trim() ?? '',
        organizationId,
        organizationName,
        role: userRole,
        license: userLicense,
        isActive: true,
      };

      let accounts: typeof currentAccountEntry[] = [currentAccountEntry];

      try {
        const storedAccounts = localStorage.getItem('medicrew_accounts');
        if (storedAccounts) {
          const parsed = JSON.parse(storedAccounts) as typeof currentAccountEntry[];
          const filteredAccounts = parsed.filter(
            (acc) => acc.accountId !== currentAccountEntry.accountId
          );
          accounts = [currentAccountEntry, ...filteredAccounts];
        }
      } catch {
        // localStorage 파싱 실패 시 현재 계정만 사용
      }

      const guestAccountExpiresAt =
        userRole === 'guest' ? (userProfile.guestAccountExpiresAt ?? null) : null;

      const userData = {
        userId: userProfile.userId,
        name: displayName,
        email: userProfile.email || '',
        profileImageUrl: userProfile.profileImageUrl,
        guestAccountExpiresAt,
        isAuthenticated: true,
        currentAccount: {
          accountId: currentAccountEntry.accountId,
          loginId: currentAccountEntry.loginId,
          displayName: currentAccountEntry.displayName,
          email: currentAccountEntry.email,
          profileImageUrl: currentAccountEntry.profileImageUrl,
          phoneNumber: currentAccountEntry.phoneNumber,
          affiliation: currentAccountEntry.affiliation,
          position: currentAccountEntry.position,
          department: currentAccountEntry.department,
          organizationId: currentAccountEntry.organizationId,
          organizationName: currentAccountEntry.organizationName,
          role: currentAccountEntry.role,
          license: currentAccountEntry.license,
        },
        accounts,
      };
      authLogin(userData);

      if (userSsoAccess.redirect?.classId) {
        const classId = userSsoAccess.redirect.classId;
        navigate(`/open-class/${classId}`);
      } else {
        navigate(ROUTES.HOME);
      }
    } catch (error: any) {
      showAlert(error.message || 'SSO 연동에 실패했습니다.');
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.HOME);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent mb-4"></div>
          <p className="text-gray-600 text-lg">정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!user || !userSsoAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <AlertDialog isOpen={alertState.isOpen} onClose={closeAlert} message={alertState.message} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-[400px] bg-white rounded-2xl p-8 shadow-[0_15px_40px_-10px_rgba(0,0,0,0.08)] border border-gray-100">
        <div className="flex justify-center mb-6">
          <img src={medicrewLogo} alt="Medicrew" className="h-7 w-auto" />
        </div>

        <h1 className="text-xl font-bold text-gray-900 mb-2 text-center">
          이미 가입된 메디크루 계정이 있습니다
        </h1>

        <p className="text-sm text-gray-600 text-center mb-6">
          동일한 이메일 또는 아이디의 메디크루 계정이 있습니다. 이 대한병원협회 계정과
          연결하시겠습니까?
        </p>

        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <div className="space-y-3">
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600">이름</span>
              <span className="text-sm font-semibold text-gray-900">{user.realName}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600">아이디</span>
              <span className="text-sm font-medium text-gray-700">{user.loginId}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-gray-600">이메일</span>
              <span className="text-sm font-medium text-gray-700">{user.email}</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Button
            type="button"
            className="w-full rounded-full text-base h-11"
            variant="primary"
            onClick={handleConnect}
            disabled={isSubmitting}
          >
            {isSubmitting ? '연동 중...' : '기존 계정과 연결'}
          </Button>

          <button
            type="button"
            className="w-full text-gray-500 hover:text-gray-900 transition-colors text-sm py-2"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            취소
          </button>
        </div>
      </div>

      <div className="mt-7 flex justify-center items-center gap-2">
        <span className="text-xs text-gray-400">Powered by</span>
        <span className="text-xs font-bold text-gray-500">NEWBASE</span>
      </div>

      <AlertDialog isOpen={alertState.isOpen} onClose={closeAlert} message={alertState.message} />
    </div>
  );
}
